import type {
  Event as SentryEvent,
  EventHint as SentryEventHint,
} from "@sentry/types";

export type ThrottleFingerprintFactory = (
  event: SentryEvent,
  hint: SentryEventHint
) => string | undefined;

export type ThrottleMatcher = (
  event: SentryEvent,
  hint: SentryEventHint
) => boolean;

export type ThrottleRule = Required<ThrottleRuleInput>;

export type ThrottleRuleInput = {
  matcher: ThrottleMatcher;
  fingerprint?: ThrottleFingerprintFactory;
  ttlMs?: number;
};

export class SentryThrottleRegistry {
  private static readonly throttledFingerprints = new Map<string, number>();
  private static readonly rules: ThrottleRule[] = [];

  static register(rule: ThrottleRuleInput) {
    this.rules.push({
      ...rule,
      ttlMs: rule.ttlMs ?? DEFAULT__THROTTLE_ONE_MINUTES,
      fingerprint: rule.fingerprint ?? defaultFingerprint,
    });
  }

  static shouldDrop(event: SentryEvent, hint: SentryEventHint) {
    const now = Date.now();

    for (const rule of this.rules) {
      if (!rule.matcher(event, hint)) {
        continue;
      }

      const key = rule.fingerprint(event, hint);
      if (!key) {
        continue;
      }

      const expiry = this.throttledFingerprints.get(key);
      if (typeof expiry === "number" && expiry > now) {
        return true;
      }

      this.throttledFingerprints.set(key, now + rule.ttlMs);
      break;
    }

    return false;
  }
}

const defaultFingerprint: ThrottleRule["fingerprint"] = (_event, hint) => {
  if (!(hint.originalException instanceof Error)) {
    return undefined;
  }
  return hint.originalException.name;
};

const DEFAULT__THROTTLE_ONE_MINUTES = 1 * 60 * 1000;
